/**
 * BFR Insights — Phase 5
 *
 * Renders ONLY for role === 'bfr'.
 * Backend: GET /api/role-insights/bfr
 *          GET /api/role-insights/drilldown?card=bfr_approved|bfr_pending&scope=global
 *
 * Layout:
 *   • 2 KPI cards (Approved Commercials, Pending Commercials) — Nx3 grid
 *   • Click a card -> details table opens beneath the cards
 *   • Click an account row -> navigates into /leads/<id> directly (the
 *     LeadDetails page renders the CommercialApproval panel where BFR can
 *     approve as long as the approval reached the BFR stage)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Hourglass, RefreshCw, Loader2, ChevronRight,
} from 'lucide-react';
import DrilldownModal, { ClickableCard } from './DrilldownModal';
import OffboardingInsightsCards from './OffboardingInsightsCards';
import DateRangeFilter, { rangeParams } from '../../components/DateRangeFilter';

const BFRInsights = ({ hideOffboardingCards = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [range, setRange] = useState('this_week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const qs = new URLSearchParams(rangeParams(range, customFrom, customTo)).toString();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/bfr?${qs}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const cards = data?.cards || {};

  return (
    <div className="space-y-5" data-testid="bfr-insights">
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">BFR Insights</h2>
          <p className="text-sm text-gray-500 mt-0.5">Commercial approvals from the BFR stage onward.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid="bfr-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="bfr-date-filter" />

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="bfr-error">{error}</div>}

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="bfr-cards-grid">
          <ClickableCard
            label="Approved Commercials"
            value={cards.approved_commercials ?? 0}
            Icon={ShieldCheck}
            accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }}
            testId="bfr-card-approved"
            onClick={() => setDrilldown({ card: 'bfr_approved', title: 'Approved Commercials' })}
          />
          <ClickableCard
            label="Pending Commercials"
            value={cards.pending_commercials ?? 0}
            Icon={Hourglass}
            accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }}
            testId="bfr-card-pending"
            onClick={() => setDrilldown({ card: 'bfr_pending', title: 'Pending Commercials' })}
          />
          <div className="bg-white rounded-xl border border-dashed border-gray-200 p-5 flex flex-col items-center justify-center text-center text-xs text-gray-400">
            <ChevronRight size={16} className="mb-1" /> Tap a card to see the underlying accounts.
          </div>
        </div>
      )}

      {drilldown && (
        <DrilldownModal
          title={drilldown.title}
          card={drilldown.card}
          scope="global"
          rangeParams={rangeParams(range, customFrom, customTo)}
          onClose={() => setDrilldown(null)}
          // BFR-specific row click -> jump straight into the account
          rowAction={{
            label: 'Open',
            onClick: (row) => { setDrilldown(null); navigate(`/leads/${row.lead_id}`); },
          }}
        />
      )}

      {/* Phase 16 — Offboarding cards (BFR-scoped to Step 4 Finance). */}
      {!hideOffboardingCards && <OffboardingInsightsCards />}
    </div>
  );
};

export default BFRInsights;
